import React, { memo, useState, useEffect } from 'react';
import { getProfile, updateProfile } from '../services/api';

function ProfileScreen({ onBack, onUpgrade, onManageSubscription }) {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [nameInput, setNameInput] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function fetchProfile() {
      try {
        const data = await getProfile();
        if (!cancelled && data) {
          setProfile(data);
          setNameInput(data.displayName || '');
        }
      } catch {
        // Profile fetch failed
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    fetchProfile();
    return () => { cancelled = true; };
  }, []);

  const handleSaveName = async () => {
    if (!nameInput.trim() || nameInput.trim().length > 20) return;
    setSaving(true);
    try {
      await updateProfile(nameInput.trim());
      setProfile((prev) => ({ ...prev, displayName: nameInput.trim() }));
      setEditing(false);
    } catch {
      // Save failed
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="profile-screen">
      <div className="profile-card">
        <div className="profile-header">
          <button className="back-btn" onClick={onBack} aria-label="Back to menu">
            &#8592;
          </button>
          <h2 className="profile-title">PROFILE</h2>
        </div>

        {loading && <div className="profile-loading">Loading...</div>}

        {!loading && profile && (
          <>
            <div className="profile-name-section">
              {editing ? (
                <div className="profile-edit-row">
                  <input
                    className="auth-input"
                    value={nameInput}
                    onChange={(e) => setNameInput(e.target.value)}
                    maxLength={20}
                    autoFocus
                  />
                  <button className="profile-save-btn" onClick={handleSaveName} disabled={saving}>
                    {saving ? '...' : 'Save'}
                  </button>
                  <button className="profile-cancel-btn" onClick={() => setEditing(false)}>
                    Cancel
                  </button>
                </div>
              ) : (
                <div className="profile-edit-row">
                  <span className="profile-display-name">{profile.displayName}</span>
                  <button className="profile-edit-btn" onClick={() => setEditing(true)}>
                    Edit
                  </button>
                </div>
              )}
              <div className="profile-email">{profile.email}</div>
            </div>

            <div className="profile-stats">
              <div className="profile-stat">
                <span className="profile-stat-value">{(profile.highScore || 0).toLocaleString()}</span>
                <span className="profile-stat-label">HIGH SCORE</span>
              </div>
              <div className="profile-stat">
                <span className="profile-stat-value">{profile.gamesPlayed || 0}</span>
                <span className="profile-stat-label">GAMES</span>
              </div>
              <div className="profile-stat">
                <span className="profile-stat-value">{profile.totalLinesCleared || 0}</span>
                <span className="profile-stat-label">LINES</span>
              </div>
              <div className="profile-stat">
                <span className="profile-stat-value">{profile.bestCombo || 0}</span>
                <span className="profile-stat-label">BEST COMBO</span>
              </div>
            </div>

            {/* Subscription & Inventory Section */}
            <div className="profile-subscription">
              <h3 className="profile-section-title">SUBSCRIPTION</h3>
              <div className="profile-sub-info">
                <div className="profile-sub-row">
                  <span className="profile-sub-label">Plan</span>
                  <span className={`profile-sub-value profile-tier-badge tier-${profile.subscriptionTier || 'free'}`}>
                    {(profile.subscriptionTier || 'free').toUpperCase().replace('_', ' ')}
                  </span>
                </div>
                {profile.subscriptionStatus === 'active' && (
                  <>
                    <div className="profile-sub-row">
                      <span className="profile-sub-label">Status</span>
                      <span className="profile-sub-value profile-sub-active">Active</span>
                    </div>
                    {profile.subscriptionExpiresAt && (
                      <div className="profile-sub-row">
                        <span className="profile-sub-label">Renews</span>
                        <span className="profile-sub-value">
                          {new Date(profile.subscriptionExpiresAt).toLocaleDateString()}
                        </span>
                      </div>
                    )}
                    {profile.subscriptionPlatform && (
                      <div className="profile-sub-row">
                        <span className="profile-sub-label">Via</span>
                        <span className="profile-sub-value">
                          App Store
                        </span>
                      </div>
                    )}
                  </>
                )}
                <div className="profile-sub-actions">
                  {profile.subscriptionTier === 'free' || !profile.subscriptionTier ? (
                    <button className="profile-upgrade-btn" onClick={onUpgrade}>
                      Upgrade to Premium
                    </button>
                  ) : (
                    <button className="profile-manage-btn" onClick={onManageSubscription}>
                      Manage Subscription
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Power-Up Inventory */}
            {profile.consumables && (
              <div className="profile-inventory">
                <h3 className="profile-section-title">POWER-UPS</h3>
                <div className="profile-stats" style={{ marginBottom: 0 }}>
                  <div className="profile-stat">
                    <span className="profile-stat-value">{profile.consumables.undos || 0}</span>
                    <span className="profile-stat-label">UNDOS</span>
                  </div>
                  <div className="profile-stat">
                    <span className="profile-stat-value">{profile.consumables.bombs || 0}</span>
                    <span className="profile-stat-label">BOMBS</span>
                  </div>
                  <div className="profile-stat">
                    <span className="profile-stat-value">{profile.consumables.refreshes || 0}</span>
                    <span className="profile-stat-label">REFRESHES</span>
                  </div>
                </div>
              </div>
            )}

            {profile.recentGames && profile.recentGames.length > 0 && (
              <div className="profile-recent">
                <h3 className="profile-section-title">RECENT GAMES</h3>
                {profile.recentGames.map((game, i) => (
                  <div key={i} className="profile-game-row">
                    <span className="profile-game-score">{game.score?.toLocaleString()}</span>
                    <span className="profile-game-lines">{game.linesCleared} lines</span>
                    <span className="profile-game-date">
                      {new Date(game.date).toLocaleDateString()}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default memo(ProfileScreen);
